import { useState } from 'react';
import { Image } from 'antd';
import {
  InfoCircleOutlined,
  FileTextOutlined,
  ToolOutlined,
  PaperClipOutlined,
} from '@ant-design/icons';

// 只读的器械限制展示组件
function ItemOptionsView({ value = [] }) {
  const rows = value;

  if (!rows || rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 flex items-center justify-center">
          <ToolOutlined className="text-2xl text-slate-400" />
        </div>
        <p className="text-slate-600 font-medium mb-1">暂无器械限制配置</p>
        <p className="text-sm text-slate-400">该项目未配置器械限制</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((row, index) => (
        <div
          key={row.id || index}
          className="flex items-center gap-3 flex-nowrap bg-white p-3 rounded-lg border border-slate-200"
        >
          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 text-sm font-medium">
            {index + 1}
          </div>
          <div className="w-30 min-w-0">
            <div className="text-sm text-slate-900 font-medium">{row.key}</div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-900">{row.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// 只读的文件列表展示组件
function FileListView({ value = [] }) {
  const [open, setOpen] = useState(false);

  if (!value || value.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 flex items-center justify-center">
          <PaperClipOutlined className="text-2xl text-slate-400" />
        </div>
        <p className="text-slate-600 font-medium mb-1">暂无项目附件</p>
        <p className="text-sm text-slate-400">该项目未上传附件</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {value.map((file, index) => {
        const { name, url: fileUrl } = file;
        const isImage = file.type.startsWith('image/');
        return (
          <div
            key={index}
            className="flex items-center gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-blue-300 hover:shadow-sm transition-all duration-200"
          >
            <div className="flex-shrink-0">
              {isImage ? (
                <div className="w-12 h-12 rounded overflow-hidden">
                  <img
                    src={fileUrl}
                    alt={name}
                    className="w-12 h-12 object-cover"
                  />
                  <Image
                    style={{ display: 'none' }}
                    alt={name}
                    src={fileUrl}
                    preview={{
                      open,
                      src: fileUrl,
                      onOpenChange: setOpen,
                    }}
                  />
                </div>
              ) : (
                <div className="w-12 h-12 bg-slate-100 rounded flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-slate-500"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm text-slate-900 font-medium truncate">
                {name}
              </div>
              <button
                onClick={() => {
                  if (isImage) {
                    setOpen(true);
                  } else {
                    const previewUrl = `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(
                      fileUrl
                    )}`;
                    window.open(previewUrl, '_blank');
                  }
                }}
                className="text-xs text-blue-600 hover:text-blue-700 cursor-pointer inline-flex items-center gap-1"
              >
                <svg
                  className="w-3 h-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                  />
                </svg>
                查看文件
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function ReviewItem({ item = {} }) {
  // Tab 管理
  const [activeTab, setActiveTab] = useState('base');

  // 判断是否为团队赛项
  const isTeamEvent =
    item?.name === '空中格斗赛' || item?.name === '空中足球赛';
  const unitLabel = isTeamEvent ? '队伍' : '人数';

  // Tab 配置
  const tabs = [
    { key: 'base', label: '基本信息', icon: InfoCircleOutlined },
    { key: 'attrs', label: '器械限制', icon: ToolOutlined },
    { key: 'rule', label: '竞赛规则', icon: FileTextOutlined },
    { key: 'appendix', label: '项目附件', icon: PaperClipOutlined },
  ];

  // 渲染 Tab 内容
  const renderTabContent = () => {
    switch (activeTab) {
      case 'base':
        return (
          <div className="space-y-6">
            <div className="flex items-start">
              <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                <span className="text-red-500 mr-1">*</span>
                竞赛项目
              </label>
              <div className="flex-1">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                  {item?.name || '-'}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                <span className="text-red-500 mr-1">*</span>
                组别
              </label>
              <div className="flex-1">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                  {item?.groupName || '-'}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                <span className="text-red-500 mr-1">*</span>
                报名费
              </label>
              <div className="flex-1">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                  {item?.applyPrice ? `¥${item?.applyPrice}` : '-'}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                <span className="text-red-500 mr-1">*</span>
                最小申请{unitLabel}
              </label>
              <div className="flex-1">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                  {item?.minApply || '-'}
                </div>
              </div>
            </div>

            <div className="flex items-start">
              <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                <span className="text-red-500 mr-1">*</span>
                最大申请{unitLabel}
              </label>
              <div className="flex-1">
                <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                  {item?.maxApply || '-'}
                </div>
              </div>
            </div>

            {isTeamEvent && (
              <>
                <div className="flex items-start">
                  <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                    <span className="text-red-500 mr-1">*</span>
                    队伍最小人数
                  </label>
                  <div className="flex-1">
                    <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                      {item?.minPlayer || '-'}
                    </div>
                  </div>
                </div>

                <div className="flex items-start">
                  <label className="block text-sm font-medium text-gray-700 min-w-30 whitespace-nowrap text-left pt-2">
                    <span className="text-red-500 mr-1">*</span>
                    队伍最大人数
                  </label>
                  <div className="flex-1">
                    <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900">
                      {item?.maxPlayer || '-'}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        );
      case 'rule':
        return (
          <div className="prose prose-sm max-w-none">
            {item?.rule &&
            item?.rule.trim() !== '' &&
            item?.rule !== '<p><br></p>' ? (
              <div
                className="px-4 py-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-900 min-h-[200px]"
                dangerouslySetInnerHTML={{ __html: item?.rule }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 mb-4 rounded-full bg-slate-100 flex items-center justify-center">
                  <FileTextOutlined className="text-2xl text-slate-400" />
                </div>
                <p className="text-slate-600 font-medium mb-1">暂无竞赛规则</p>
                <p className="text-sm text-slate-400">该项目未配置竞赛规则</p>
              </div>
            )}
          </div>
        );
      case 'attrs':
        return <ItemOptionsView value={item?.attrsList} />;
      case 'appendix':
        return <FileListView value={item?.appendix} />;
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* 上部：横向 Tab */}
      <div className="flex border-b border-slate-200">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 pb-3 text-center transition-colors cursor-pointer ${
                activeTab === tab.key
                  ? 'text-blue-600 border-b-2 border-blue-600 font-medium'
                  : 'text-slate-600 hover:text-blue-600'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <Icon className="text-base" />
                <span>{tab.label}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* 中部：可滚动内容区 */}
      <div className="flex-1 overflow-y-auto p-6">{renderTabContent()}</div>
    </div>
  );
}
